import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowRight, Phone, Star } from "lucide-react";
import { Reveal, MaskLine } from "../components/Reveal";
import { BeforeAfter } from "../components/BeforeAfter";
import { Marquee } from "../components/Marquee";
import { BRAND, SERVICES, CHAPTERS, PROCESS, REVIEWS, TRANSFORMATIONS, COLUMNS, IMAGES } from "../data/site";

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.55, 0.85]);

  return (
    <section ref={ref} data-testid="hero" className="relative h-[100svh] w-full overflow-hidden">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img
          src={IMAGES.heroStair}
          alt="Illuminated flagstone stairway at dusk"
          className="h-full w-full object-cover"
        />
      </motion.div>
      <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 bg-obsidian" />
      <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/20 to-obsidian/40" />

      <div className="relative z-10 mx-auto flex h-full max-w-[1500px] flex-col justify-end px-6 pb-16 md:px-12 md:pb-24">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 1 }}
          className="mb-6 flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] text-sand"
        >
          <span className="h-px w-10 bg-ember" />
          {BRAND.location} · Hardscape & Landscape
        </motion.p>

        <h1 className="font-display text-[13vw] font-light leading-[0.92] tracking-tight text-white sm:text-[10vw] lg:text-[8.5vw]">
          <MaskLine delay={0.25}>The backyard</MaskLine>
          <MaskLine delay={0.4}>
            you <span className="italic text-sand">dream</span> about,
          </MaskLine>
          <MaskLine delay={0.55}>
            <span className="text-ember">built right.</span>
          </MaskLine>
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.9 }}
          className="mt-10 flex flex-col items-start gap-6 sm:flex-row sm:items-center"
        >
          <Link
            to="/projects#contact"
            data-testid="hero-cta"
            className="group inline-flex items-center gap-3 bg-white px-8 py-4 text-[13px] uppercase tracking-[0.18em] text-obsidian transition-colors duration-300 hover:bg-ember"
          >
            Get a free estimate
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
          <a
            href={BRAND.phoneHref}
            data-testid="hero-call"
            className="inline-flex items-center gap-3 text-[13px] uppercase tracking-[0.18em] text-white transition-colors hover:text-ember"
          >
            <Phone className="h-4 w-4" /> {BRAND.phone}
          </a>
        </motion.div>
      </div>
    </section>
  );
};

const Intro = () => (
  <section className="mx-auto max-w-[1500px] px-6 py-24 md:px-12 md:py-40">
    <div className="grid grid-cols-1 gap-12 md:grid-cols-12">
      <Reveal className="md:col-span-3">
        <p className="text-[11px] uppercase tracking-[0.25em] text-ember">Who we are</p>
      </Reveal>
      <div className="md:col-span-9">
        <Reveal>
          <p className="font-display text-3xl font-light leading-[1.3] text-white sm:text-4xl lg:text-5xl">
            We do custom hardscape and landscape work — outdoor patios, fireplaces,
            fire pits and complete backyard transformations. We also help homeowners
            and realtors get houses <span className="italic text-sand">market-ready</span> and
            sold faster.
          </p>
        </Reveal>
      </div>
    </div>
  </section>
);

const Services = () => (
  <section id="services" data-testid="services" className="border-t border-border">
    <div className="mx-auto max-w-[1500px] px-6 md:px-12">
      <div className="flex flex-col justify-between gap-6 py-16 sm:flex-row sm:items-end">
        <Reveal>
          <h2 className="font-display text-4xl font-light text-white sm:text-5xl">
            What we build
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="max-w-sm text-base font-light text-sand">
            Full-service outdoor and interior construction — from the first cut of
            the land to the last stone set.
          </p>
        </Reveal>
      </div>

      <div className="grid grid-cols-1 border-t border-border sm:grid-cols-2 lg:grid-cols-4">
        {SERVICES.map((s, i) => (
          <Reveal
            key={s.n}
            delay={(i % 4) * 0.06}
            className="group relative border-b border-border p-8 transition-colors duration-500 hover:bg-surface sm:[&:nth-child(odd)]:border-r lg:[&:not(:nth-child(4n))]:border-r"
          >
            <span className="font-display text-sm text-ember">{s.n}</span>
            <h3 className="mt-5 font-display text-2xl font-light text-white">{s.title}</h3>
            <p className="mt-3 text-sm font-light leading-relaxed text-sand">{s.desc}</p>
            <ArrowUpRight className="mt-6 h-5 w-5 text-sand opacity-0 transition-all duration-300 group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:text-ember group-hover:opacity-100" />
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Approach = () => (
  <section id="approach" data-testid="approach" className="mx-auto max-w-[1500px] px-6 py-24 md:px-12 md:py-40">
    <Reveal>
      <p className="text-[11px] uppercase tracking-[0.25em] text-ember">The 101 approach</p>
      <h2 className="mt-6 max-w-3xl font-display text-4xl font-light leading-tight text-white sm:text-5xl">
        Real crews. Real craftsmanship. Every stone set by hand.
      </h2>
    </Reveal>

    <div className="mt-16 grid grid-cols-1 gap-6 md:grid-cols-3">
      {PROCESS.map((p, i) => (
        <Reveal key={p.step} delay={i * 0.1} className="group">
          <div className="relative aspect-[4/5] overflow-hidden">
            <img
              src={p.src}
              alt={p.label}
              className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-obsidian/80 to-transparent" />
            <span className="absolute left-5 top-5 font-display text-lg text-ember">{`0${i + 1}`}</span>
            <div className="absolute bottom-5 left-5 right-5">
              <p className="text-[11px] uppercase tracking-[0.25em] text-sand">{p.step}</p>
              <p className="mt-1 font-display text-xl font-light text-white">{p.label}</p>
            </div>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

const Chapters = () => (
  <section className="border-y border-border bg-surface">
    <div className="mx-auto max-w-[1500px] px-6 md:px-12">
      {CHAPTERS.map((c, i) => (
        <Reveal
          key={c.n}
          className={`grid grid-cols-1 gap-8 py-16 md:grid-cols-12 md:py-24 ${
            i !== CHAPTERS.length - 1 ? "border-b border-border" : ""
          }`}
        >
          <div className="md:col-span-2">
            <span className="font-display text-6xl font-light text-outline-accent">{c.n}</span>
          </div>
          <div className="md:col-span-6">
            <h3 className="font-display text-3xl font-light leading-tight text-white sm:text-4xl">
              {c.title}
            </h3>
          </div>
          <div className="md:col-span-4">
            <p className="text-base font-light leading-relaxed text-sand">{c.body}</p>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

const Realtor = () => (
  <section data-testid="realtor" className="relative overflow-hidden">
    <div className="mx-auto grid max-w-[1500px] grid-cols-1 items-stretch lg:grid-cols-2">
      <div className="flex flex-col justify-center px-6 py-20 md:px-12 md:py-32">
        <Reveal>
          <p className="text-[11px] uppercase tracking-[0.25em] text-ember">For realtors & sellers</p>
          <h2 className="mt-6 font-display text-4xl font-light leading-tight text-white sm:text-5xl lg:text-6xl">
            Get your house
            <br />
            <span className="italic text-sand">market-ready.</span>
          </h2>
          <p className="mt-8 max-w-md text-base font-light leading-relaxed text-sand">
            First impressions sell homes. We handle the remodels, curb appeal,
            hardscaping and clean-ups that help realtors and homeowners list
            faster, show better and close for more.
          </p>
          <Link
            to="/projects#contact"
            data-testid="realtor-cta"
            className="group mt-10 inline-flex items-center gap-3 border border-border px-8 py-4 text-[13px] uppercase tracking-[0.18em] text-white transition-colors duration-300 hover:border-ember hover:text-ember"
          >
            Talk about your listing
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
      <div className="relative min-h-[380px] overflow-hidden">
        <img
          src={IMAGES.lawn}
          alt="Fresh landscaping boosting curb appeal"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-obsidian/20" />
      </div>
    </div>
  </section>
);

const Featured = () => {
  const feat = [
    { src: IMAGES.firepitAutumn, tag: "Flagstone Fire Pit" },
    { src: IMAGES.paverFirepit, tag: "Paver Patio & Fire Pit" },
    { src: IMAGES.poolClouds, tag: "Backyard Transformation" },
  ];
  return (
    <section className="mx-auto max-w-[1500px] px-6 py-24 md:px-12 md:py-40">
      <div className="flex flex-col justify-between gap-6 sm:flex-row sm:items-end">
        <Reveal>
          <h2 className="font-display text-4xl font-light text-white sm:text-5xl">
            Finished projects
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <Link
            to="/projects"
            data-testid="featured-viewall"
            className="group inline-flex items-center gap-2 text-[13px] uppercase tracking-[0.18em] text-sand transition-colors hover:text-ember"
          >
            View all projects
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>

      <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-3 hover-dim">
        {feat.map((f, i) => (
          <Reveal key={i} delay={i * 0.1}>
            <Link to="/projects" className="group block">
              <div className="dim-target relative aspect-[3/4] overflow-hidden transition-opacity duration-500">
                <img
                  src={f.src}
                  alt={f.tag}
                  className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-obsidian/70 to-transparent opacity-60" />
                <div className="absolute bottom-6 left-6 flex items-center gap-3">
                  <span className="font-display text-xl text-white">{`0${i + 1}`}</span>
                  <span className="text-[11px] uppercase tracking-[0.2em] text-sand">{f.tag}</span>
                </div>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
};

const Reviews = () => (
  <section data-testid="reviews" className="border-y border-border bg-surface">
    <div className="mx-auto max-w-[1500px] px-6 py-24 md:px-12 md:py-32">
      <div className="grid grid-cols-1 gap-12 md:grid-cols-12">
        <Reveal className="md:col-span-4">
          <p className="text-[11px] uppercase tracking-[0.25em] text-ember">Reviews</p>
          <div className="mt-6 flex items-end gap-4">
            <span className="font-display text-7xl font-light leading-none text-white">5.0</span>
            <div className="pb-2">
              <div className="flex gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-4 w-4 fill-ember text-ember" />
                ))}
              </div>
              <p className="mt-1 text-sm text-sand">Google · 2 reviews</p>
            </div>
          </div>
          <p className="mt-8 max-w-xs text-base font-light leading-relaxed text-sand">
            Real feedback from homeowners across {BRAND.location}. We build for
            trust — and it shows.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 gap-6 md:col-span-8 md:grid-cols-2">
          {REVIEWS.map((r, i) => (
            <Reveal
              key={r.name}
              delay={i * 0.1}
              className="flex flex-col border border-border bg-obsidian p-8"
            >
              <div className="flex gap-1">
                {[...Array(5)].map((_, s) => (
                  <Star key={s} className="h-3.5 w-3.5 fill-ember text-ember" />
                ))}
              </div>
              <p className="mt-6 flex-1 text-base font-light leading-relaxed text-white/90">
                "{r.text}"
              </p>
              <div className="mt-8 flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-surface2 font-display text-sm text-ember">
                  {r.name[0]}
                </span>
                <div>
                  <p className="text-sm text-white">{r.name}</p>
                  <p className="text-xs text-sand">{r.when}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const BeforeAfterSection = () => (
  <section id="before-after" data-testid="before-after" className="mx-auto max-w-[1500px] px-6 py-24 md:px-12 md:py-32">
    <Reveal className="mb-14">
      <p className="text-[11px] uppercase tracking-[0.25em] text-ember">Before / After</p>
      <h2 className="mt-4 max-w-2xl font-display text-4xl font-light leading-tight text-white sm:text-5xl">
        The transformations we live for.
      </h2>
      <p className="mt-6 max-w-xl text-base font-light leading-relaxed text-sand">
        Drag the slider to see the difference — real projects, start to finish.
      </p>
    </Reveal>
    <div className="grid grid-cols-1 gap-10 lg:grid-cols-2">
      {TRANSFORMATIONS.map((t, i) => (
        <Reveal key={t.title} delay={i * 0.1}>
          <BeforeAfter {...t} />
        </Reveal>
      ))}
    </div>
  </section>
);

const Columns = () => (
  <section data-testid="columns" className="border-t border-border">
    <div className="mx-auto max-w-[1500px] px-6 py-24 md:px-12 md:py-32">
      <Reveal className="mb-14">
        <p className="text-[11px] uppercase tracking-[0.25em] text-ember">What we mostly do</p>
        <h2 className="mt-4 font-display text-4xl font-light text-white sm:text-5xl">
          Built by hand, on every job.
        </h2>
      </Reveal>
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-5">
        {COLUMNS.map((c, i) => (
          <Reveal key={c.title} delay={i * 0.1} className="group">
            <div className="relative aspect-[4/5] overflow-hidden">
              <img
                src={c.src}
                alt={c.title}
                className={`h-full w-full object-cover transition-transform duration-700 ease-out ${c.rotate ? "rotate-90 scale-[1.6]" : "group-hover:scale-105"}`}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-obsidian/85 via-obsidian/10 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-7">
                <p className="text-[11px] uppercase tracking-[0.25em] text-ember">{c.tag}</p>
                <h3 className="mt-2 font-display text-2xl font-light text-white">{c.title}</h3>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export default function Home() {
  return (
    <>
      <Hero />
      <Marquee />
      <Intro />
      <Services />
      <Approach />
      <Chapters />
      <BeforeAfterSection />
      <Columns />
      <Realtor />
      <Reviews />
      <Featured />
    </>
  );
}
